import type { CfbScheduleApprovalStatus, CfbScheduleGame } from './cfbScheduleTypes';

export function formatCfbScheduleStatus(status: CfbScheduleApprovalStatus | string | undefined): string {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'locked':
      return 'Locked';
    case 'draft':
    default:
      return 'Draft';
  }
}

export function groupCfbScheduleGamesByWeek(games: CfbScheduleGame[]): Map<number, CfbScheduleGame[]> {
  const grouped = new Map<number, CfbScheduleGame[]>();
  for (const game of games) {
    const week = Number(game.week_number ?? 0);
    const bucket = grouped.get(week) ?? [];
    bucket.push(game);
    grouped.set(week, bucket);
  }
  return grouped;
}

export function formatCfbScheduleDateTime(value?: string | null): string {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatCfbScheduleMatchup(game: CfbScheduleGame): string {
  const home = game.home_school_name ?? 'Home';
  const away = game.away_school_name ?? 'Away';
  return `${away} @ ${home}`;
}

export function resolveEffectiveDynastySeasonId(
  dynastySeasonId?: string | null,
  season?: { id?: string | null } | null
): string {
  const fromState = (dynastySeasonId ?? '').trim();
  if (fromState) {
    return fromState;
  }
  return (season?.id ?? '').trim();
}

export function validateCfbScheduleGameInput(input: {
  dynastySeasonId?: string;
  weekNumber: number;
  homeAssignmentId: string;
  awayAssignmentId: string;
}): string | null {
  if (!resolveEffectiveDynastySeasonId(input.dynastySeasonId)) {
    return 'Dynasty season context is missing. Reload the page or select a season.';
  }
  if (!Number.isFinite(input.weekNumber) || input.weekNumber < 1) {
    return 'Week number must be a positive integer.';
  }
  if (!input.homeAssignmentId || !input.awayAssignmentId) {
    return 'Select home and away school assignments.';
  }
  if (input.homeAssignmentId === input.awayAssignmentId) {
    return 'Home and away assignments must be different.';
  }
  return null;
}

const INTERNAL_MEMBER_ID = /^member-[0-9a-f-]{36}$/i;

export function containsInternalMemberId(text: string): boolean {
  return INTERNAL_MEMBER_ID.test(text.trim());
}
